import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import EditorJS, { OutputData } from "@editorjs/editorjs";
import Header from "@editorjs/header";
import EditorjsList from "@editorjs/list";
import Quote from "@editorjs/quote";
import CodeTool from "@editorjs/code";
import Table from "@editorjs/table";
import InlineCode from "@editorjs/inline-code";
import Undo from "editorjs-undo";
import DragDrop from "editorjs-drag-drop";

import type {
  EditorContent,
  NewsMediaAsset,
  NewsVideoAsset,
} from "../../services/newsService/newsService.types";
import {
  DuplicateBlockTune,
  NewsDelimiterTool,
  NewsLinkTool,
  NewsMediaTool,
  NewsVideoTool,
  StrikeInlineTool,
  UnderlineInlineTool,
} from "./NewsEditorTools";
import { normalizeNewsEditorContent } from "./newsEditorContent";

export interface NewsBlockEditorHandle {
  save: () => Promise<EditorContent>;
  undo: () => void;
  redo: () => void;
}

interface NewsBlockEditorProps {
  articleId: number;
  data: EditorContent;
  uploadMedia: (file: File) => Promise<NewsMediaAsset>;
  uploadVideo: (file: File) => Promise<NewsVideoAsset>;
  onChange?: () => void;
}

const NewsBlockEditor = forwardRef<NewsBlockEditorHandle, NewsBlockEditorProps>(
  ({ articleId, data, uploadMedia, uploadVideo, onChange }, ref) => {
    const holderRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<EditorJS | null>(null);
    const undoRef = useRef<Undo | null>(null);

    useEffect(() => {
      if (!holderRef.current) return;

      const notifierObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            const candidates = [
              node,
              ...Array.from(node.querySelectorAll<HTMLElement>(".cdx-notify, .cdx-notify__message")),
            ];
            candidates.forEach((candidate) => {
              if (candidate.textContent?.trim() === "Pasted link is not valid.") {
                candidate.textContent = "Ссылка указана некорректно.";
              }
            });
          });
        });
      });
      notifierObserver.observe(document.body, { childList: true, subtree: true });

      const editorData = normalizeNewsEditorContent(data);

      const editor = new EditorJS({
        holder: holderRef.current,
        data: editorData as OutputData,
        minHeight: 380,
        placeholder: "Начните писать статью или добавьте блок через +",
        inlineToolbar: ["bold", "italic", "link", "inlineCode", "underline", "strikethrough"],
        i18n: {
          messages: {
            ui: {
              blockTunes: {
                toggler: {
                  "Click to tune": "Настроить блок",
                  "or drag to move": "или перетащите для перемещения",
                },
              },
              inlineToolbar: {
                converter: {
                  "Convert to": "Преобразовать в",
                },
              },
              toolbar: {
                toolbox: {
                  Add: "Добавить",
                },
              },
              popover: {
                Filter: "Поиск",
                "Nothing found": "Ничего не найдено",
                "Convert to": "Преобразовать в",
              },
            },
            toolNames: {
              Text: "Текст",
              Heading: "Заголовок",
              "Unordered List": "Маркированный список",
              "Ordered List": "Нумерованный список",
              Checklist: "Чек-лист",
              Quote: "Цитата",
              Delimiter: "Разделитель",
              Code: "Код",
              Table: "Таблица",
              Link: "Ссылка",
              Bold: "Жирный",
              Italic: "Курсив",
              "Inline Code": "Встроенный код",
            },
            tools: {
              link: {
                "Add a link": "Вставьте ссылку",
              },
              stub: {
                Error: "Ошибка",
                "The block can not be displayed correctly.": "Не удалось корректно отобразить блок.",
              },
              list: {
                Unordered: "Маркированный",
                Ordered: "Нумерованный",
                Checklist: "Чек-лист",
                "Start with": "Начать с",
                "Counter type": "Тип нумерации",
                Numeric: "Цифры",
                "Lower Roman": "Римские, строчные",
                "Upper Roman": "Римские, прописные",
                "Lower Alpha": "Буквы, строчные",
                "Upper Alpha": "Буквы, прописные",
              },
              table: {
                "Add column to left": "Добавить столбец слева",
                "Add column to right": "Добавить столбец справа",
                "Delete column": "Удалить столбец",
                "Add row above": "Добавить строку сверху",
                "Add row below": "Добавить строку снизу",
                "Delete row": "Удалить строку",
                Heading: "Заголовок",
                "With headings": "С заголовком",
                "Without headings": "Без заголовка",
                Collapse: "Свернуть",
                Stretch: "Растянуть",
              },
            },
            blockTunes: {
              delete: {
                Delete: "Удалить",
                "Click to delete": "Нажмите для удаления",
              },
              moveUp: {
                "Move up": "Переместить вверх",
              },
              moveDown: {
                "Move down": "Переместить вниз",
              },
            },
          },
        },
        tunes: ["duplicateBlock"],
        tools: {
          duplicateBlock: DuplicateBlockTune as any,
          header: {
            class: Header as any,
            inlineToolbar: true,
            config: {
              levels: [1, 2, 3],
              defaultLevel: 2,
              placeholder: "Заголовок",
            },
          },
          list: {
            class: EditorjsList as any,
            inlineToolbar: true,
          },
          quote: {
            class: Quote as any,
            inlineToolbar: true,
            config: {
              quotePlaceholder: "Текст цитаты",
              captionPlaceholder: "Автор / источник",
            },
          },
          delimiter: NewsDelimiterTool as any,
          code: {
            class: CodeTool as any,
            config: { placeholder: "Вставьте код" },
          },
          table: {
            class: Table as any,
            inlineToolbar: true,
            config: { rows: 2, cols: 3 },
          },
          inlineCode: InlineCode as any,
          underline: UnderlineInlineTool as any,
          strikethrough: StrikeInlineTool as any,
          newsMedia: {
            class: NewsMediaTool as any,
            config: { uploadMedia },
          },
          newsVideo: {
            class: NewsVideoTool as any,
            config: { uploadVideo },
          },
          newsLink: NewsLinkTool as any,
        },
        onReady: () => {
          const undo = new Undo({ editor });
          undo.initialize(editorData as OutputData);
          undoRef.current = undo;
          new DragDrop(editor);
        },
        onChange: () => {
          if (onChange) onChange();
        },
      });

      editorRef.current = editor;
      return () => {
        notifierObserver.disconnect();
        undoRef.current = null;
        editorRef.current = null;
        void editor.isReady
          .then(() => editor.destroy())
          .catch(() => undefined);
      };
      // Editor.js is stateful: recreate it only when the article itself changes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [articleId]);

    useImperativeHandle(ref, () => ({
      save: async () => {
        if (!editorRef.current) return normalizeNewsEditorContent(data);
        const output = await editorRef.current.save();
        return output as EditorContent;
      },
      undo: () => {
        if (undoRef.current) undoRef.current.undo();
      },
      redo: () => {
        if (undoRef.current) undoRef.current.redo();
      },
    }));

    return <div className="news-editor-holder" ref={holderRef} />;
  },
);

NewsBlockEditor.displayName = "NewsBlockEditor";

export default NewsBlockEditor;